import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useOnboarding } from "@/hooks/useOnboarding";

import {
  AnnouncementBanner,
  LandingHeader,
  LandingHero,
  LandingProblem,
  LandingSolution,
  LandingJourCritique,
  LandingHowItWorks,
  LandingResults,
  LandingBlog,
  LandingFAQ,
  LandingCTAFinal,
  LandingFooter,
} from "@/components/landing";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "B8ild",
  url: "https://b8ild.fr",
  logo: "https://b8ild.fr/logo-b8ild.png",
  description: "Logiciel de suivi de rentabilité chantier pour les professionnels du BTP",
  foundingDate: "2024",
  sameAs: ["https://www.linkedin.com/company/b8ild"],
  email: "contact@b8ild.fr",
  contactPoint: {
    "@type": "ContactPoint",
    url: "https://b8ild.fr/contact",
    contactType: "customer service",
    availableLanguage: "fr",
  },
};

const softwareSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "B8ild",
  url: "https://b8ild.fr",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: "Logiciel de gestion financière et suivi de rentabilité chantier pour artisans et entreprises du BTP",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "EUR",
    description: "Essai gratuit 14 jours sans carte bancaire",
  },
  featureList: [
    "Suivi de rentabilité chantier en temps réel",
    "Calcul du jour critique",
    "Gestion des retards et pénalités",
    "OCR et scan de factures par IA",
    "Planning et gestion des équipes",
    "Analyse IA des chantiers",
    "Rapports PDF automatisés",
    "Trésorerie prévisionnelle",
  ],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "B8ild est-il adapté à mon métier ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "B8ild est conçu pour tous les professionnels du BTP : électriciens, plombiers, maçons, peintres, couvreurs, entreprises générales... Quelle que soit votre spécialité, l'outil s'adapte à votre façon de travailler.",
      },
    },
    {
      "@type": "Question",
      name: "Combien de temps pour prendre en main l'outil ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "10 minutes suffisent. Aucune formation nécessaire. L'interface est intuitive et pensée pour les artisans, pas pour les équipes IT. Créez votre premier chantier, importez une facture, et vous êtes opérationnel.",
      },
    },
    {
      "@type": "Question",
      name: "Mes données sont-elles sécurisées ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Oui. B8ild est hébergé en France sur des serveurs sécurisés et conforme RGPD. Vos données vous appartiennent. Elles sont chiffrées en transit et au repos. Vous pouvez les exporter ou les supprimer à tout moment.",
      },
    },
    {
      "@type": "Question",
      name: "Puis-je annuler à tout moment ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Oui, sans engagement. Vous pouvez annuler votre abonnement en un clic depuis les paramètres. Aucune pénalité, aucune question posée. Vos données restent accessibles pendant 30 jours après annulation.",
      },
    },
    {
      "@type": "Question",
      name: "Puis-je gérer des équipes qui tournent sur plusieurs chantiers ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Oui. B8ild gère les affectations par demi-journée. Les coûts de main d'œuvre sont calculés automatiquement pour chaque chantier, même avec des rotations fréquentes. Vous voyez en temps réel qui travaille où.",
      },
    },
    {
      "@type": "Question",
      name: "Puis-je importer mes anciens chantiers ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Oui, vous pouvez créer vos chantiers en cours et importer vos factures existantes. Le Scan IA extrait automatiquement les informations. Vous pouvez aussi renseigner manuellement les coûts déjà engagés.",
      },
    },
    {
      "@type": "Question",
      name: "Quelle est la marge moyenne dans le BTP ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "La marge brute dans le BTP va de 25% à 50% selon le type de travaux. En rénovation courante : 30-50%. En dépannage ou urgence : 50-70%. En construction neuve et gros œuvre : 25-40%. Mais attention, la marge nette — ce qui reste après les frais généraux (loyer, assurances, véhicule, comptable) — tombe entre 3% et 8% en moyenne nationale selon la FFB. Prenez un plombier-chauffagiste qui fait 250 000€ de CA avec 35% de marge brute. Sa marge nette tourne autour de 6%, soit 15 000€ de bénéfice net annuel. Un seul chantier déficitaire de 5 000€ lui mange un tiers de son résultat. B8ild affiche la marge nette de chaque chantier en temps réel. Pas besoin d'attendre le bilan comptable pour savoir lequel vous coûte de l'argent.",
      },
    },
    {
      "@type": "Question",
      name: "Comment calculer le coût de revient d'un chantier ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "La formule : déboursé sec (matériaux + main d'œuvre directe + sous-traitance) + frais de chantier (location matériel, benne, déplacements) + quote-part de frais généraux. Exemple concret : un chantier de rénovation à 45 000€ HT. Matériaux : 12 000€. Main d'œuvre : 3 ouvriers pendant 15 jours à 180€/jour chacun = 8 100€. Sous-traitance plaquiste : 3 500€. Frais de chantier : 1 200€. Frais généraux (15% du déboursé sec) : 3 720€. Coût de revient total : 28 520€. Marge prévisionnelle : 16 480€, soit 36,6%. Mais si les travaux durent 20 jours au lieu de 15, le coût main d'œuvre passe à 10 800€ et la marge fond à 31%. B8ild fait ce calcul en continu, à chaque facture importée.",
      },
    },
    {
      "@type": "Question",
      name: "C'est quoi la différence entre marge brute et marge nette ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "La marge brute = chiffre d'affaires moins les coûts directs (matériaux, main d'œuvre, sous-traitance). La marge nette = marge brute moins les frais généraux (loyer atelier, assurance décennale, véhicule, comptable, téléphone). Exemple : un électricien fait un chantier à 8 000€ HT. Matériaux : 2 500€. Main d'œuvre : 2 200€. Marge brute : 3 300€, soit 41%. Il retire sa quote-part de frais généraux : 1 400€. Marge nette : 1 900€, soit 23,7%. C'est la marge nette qui compte. Un chantier à 40% de marge brute peut être à 5% de marge nette si vos frais généraux sont élevés. B8ild affiche les deux, chantier par chantier.",
      },
    },
    {
      "@type": "Question",
      name: "Faut-il un logiciel pour un artisan seul ?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Oui, dès 2 chantiers en parallèle. Un artisan seul qui gère 3 chantiers simultanés avec Excel passe 4 à 6 heures par semaine sur le suivi administratif. C'est 200 à 300 heures par an. À 45€/h de taux horaire, ça représente 9 000 à 13 500€ de temps non facturé. Pire : sans suivi temps réel, vous découvrez les problèmes trop tard. Un maçon indépendant qui fait 120 000€ de CA et qui rate une dérive de 3 000€ sur un chantier perd 15% de son résultat net annuel. Avec B8ild, vous importez vos factures en 30 secondes. L'app calcule vos marges. Vous voyez d'un coup d'œil si un chantier dérape. Le gain : moins de paperasse, plus de marge, zéro mauvaise surprise.",
      },
    },
  ],
};

const LandingPage = () => {
  usePageMeta({ title: "B8ild — Suivi rentabilité chantier BTP temps réel", description: "Logiciel de gestion financière pour artisans et entreprises du BTP. Suivez la rentabilité de vos chantiers en temps réel. Essai gratuit 14 jours.", url: "https://b8ild.fr/" });
  const navigate = useNavigate();
  const { user } = useAuth();
  const { checkOnboardingCompleted } = useOnboarding();

  // Rediriger les utilisateurs connectés vers onboarding ou dashboard
  useEffect(() => {
    if (user) {
      if (!checkOnboardingCompleted()) {
        navigate("/onboarding");
      } else {
        navigate("/dashboard");
      }
    }
  }, [user, navigate, checkOnboardingCompleted]);

  return (
    <div className="min-h-screen bg-[#0F172A] overflow-x-hidden">
      <JsonLd data={organizationSchema} />
      <JsonLd data={softwareSchema} />
      <JsonLd data={faqSchema} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "B8ild",
        url: "https://b8ild.fr",
        description: "Logiciel de suivi de rentabilité chantier pour artisans et PME du BTP",
        inLanguage: "fr",
      }} />
      <LandingHeader />
      <main>
        <LandingHero />
        <LandingProblem />
        <LandingSolution />
        <LandingJourCritique />
        <LandingHowItWorks />
        <LandingResults />
        <LandingFAQ />
        <LandingBlog />
        <LandingCTAFinal />
      </main>
      <LandingFooter />
    </div>
  );
};

export default LandingPage;
